import { useState } from 'react'
import { 
  Box, 
  Text, 
  VStack, 
  HStack, 
  Button, 
  Badge, 
  Heading, 
  Icon, 
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Input,
  Spinner,
  Alert,
  AlertIcon,
  useToast,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Tooltip,
  Tag,
  TagLabel,
  TagLeftIcon,
  IconButton,
} from '@chakra-ui/react'
import { ChatIcon, ViewIcon, RepeatIcon, EditIcon, SettingsIcon, AttachmentIcon } from '@chakra-ui/icons'
import { AiFillStar, AiOutlineStar } from 'react-icons/ai'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useAuth } from '../contexts/AuthContext'
import { submissionHistoryService, getSubmissionName, hasCustomName, getDisplayName, isSubmissionBookmarked } from '../services/submissionHistoryService'
import { SubmissionList, EnhancedPublicSubmissionData } from '../types/unified_models'
// Use a lightweight code block for viewing submission source to keep modal snappy

import StdoutDisplay from './StdoutDisplay'
import ResultsOverview from './ResultsOverview'
import { normalizeCombinedResponseInputs } from '../utils/normalizeResponse'
import CorrectnessTestModule from './CorrectnessTestModule'
import BenchmarkModule from './BenchmarkModule'
import ProfilerModule from './ProfilerModule'
import ParallelAnalysisModule from './ParallelAnalysisModule'
import SubmissionSparkChart from './SubmissionSparkChart'

interface SubmissionHistoryProps {
  problemId: string
  onRestoreSubmission?: (sourceCode: string, response: any) => void
}

interface ViewSubmissionModalProps {
  isOpen: boolean
  onClose: () => void
  submissionData: EnhancedPublicSubmissionData | null
  submissionName: string
  onRestore: (sourceCode: string, response: any) => void
  isLoading: boolean
}

function ViewSubmissionModal({ isOpen, onClose, submissionData, submissionName, onRestore, isLoading }: ViewSubmissionModalProps) {
  const textColor = useColorModeValue('gray.800', 'gray.200')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const bgColor = useColorModeValue('white', 'gray.800')
  const [isRestoring, setIsRestoring] = useState(false)
  const [selectedTab, setSelectedTab] = useState(0)

  const handleRestore = () => {
    if (!submissionData) return
    setIsRestoring(true)
    onRestore(submissionData.source_code, submissionData.response)
    setIsRestoring(false)
    onClose()
  }

  if (!isOpen) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color={textColor}>
          <HStack>
            <Icon as={ViewIcon} color="blue.400" />
            <Text>Viewing: {submissionName}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Code Display */}
            <Box>
              <Heading size="sm" color={textColor} mb={3}>Source Code</Heading>
              {isLoading ? (
                <Box display="flex" justifyContent="center" py={8}>
                  <Spinner size="md" />
                </Box>
              ) : submissionData?.source_code ? (
                <Box
                  as="pre"
                  border="1px"
                  borderColor={borderColor}
                  borderRadius="md"
                  p={3}
                  bg="gray.50"
                  fontSize="xs"
                  fontFamily="mono"
                  overflowX="auto"
                  maxH="300px"
                  overflowY="auto"
                  whiteSpace="pre-wrap"
                  wordBreak="break-word"
                >
                  {submissionData.source_code}
                </Box>
              ) : (
                <Box border="1px" borderColor={borderColor} borderRadius="md" p={4} bg="gray.50">
                  <Text color="gray.500" textAlign="center">
                    No source code available for this submission
                  </Text>
                </Box>
              )}
            </Box>

            <Divider />

            {/* Results Display with Tabs - Similar to Right Column */}
            <Box>
              <Heading size="sm" color={textColor} mb={3}>Results</Heading>
              <Tabs index={selectedTab} onChange={setSelectedTab} variant="enclosed" isLazy lazyBehavior="keepMounted">
                <TabList>
                  <Tab fontSize="sm">Overview</Tab>
                  <Tab fontSize="sm">Tests</Tab>
                  <Tab fontSize="sm">Performance</Tab>
                  <Tab fontSize="sm">Analysis</Tab>
                  <Tab fontSize="sm">Output</Tab>
                </TabList>

                <TabPanels>
                  {/* Overview Tab */}
                  <TabPanel p={4} bg={bgColor}>
                    <ResultsOverview 
                      response={submissionData?.response || null} 
                      onNavigateToTab={(tabIndex) => setSelectedTab(tabIndex + 1)} // +1 because overview is tab 0
                    />
                  </TabPanel>

                  {/* Tests Tab - Correctness Only */}
                  <TabPanel p={4} bg={bgColor}>
                    <CorrectnessTestModule correctness={submissionData?.response?.correctness || null} />
                  </TabPanel>

                  {/* Performance Tab - Benchmarks with proper filtering */}
                  <TabPanel p={4} bg={bgColor}>
                    {submissionData && (
                      <BenchmarkModule 
                        benchmark={submissionData.response?.benchmark || null}
                        activeInput={submissionData.input_targets?.[0] || null}
                        limitToActiveInput={Boolean(submissionData.input_targets && submissionData.input_targets.length === 1)}
                      />
                    )}
                  </TabPanel>

                  {/* Analysis Tab - Profiler and Parallel Analysis */}
                  <TabPanel p={4} bg={bgColor}>
                    <VStack spacing={4} align="stretch">
                      {submissionData?.response?.profiler && (
                        <ProfilerModule 
                          profiler={submissionData.response.profiler}
                          benchmark={submissionData.response.benchmark || null}
                        />
                      )}
                      {(submissionData?.response?.cilksan || submissionData?.response?.cilkscale_benchmark) && (
                        <ParallelAnalysisModule 
                          cilksan={submissionData?.response?.cilksan || null} 
                          cilkscaleBenchmark={submissionData?.response?.cilkscale_benchmark || null} 
                          sourceCode={submissionData?.source_code || ''} 
                        />
                      )}
                      {!submissionData?.response?.profiler && !submissionData?.response?.cilksan && !submissionData?.response?.cilkscale_benchmark && (
                        <Box textAlign="center" py={8} color="gray.500">
                          <Text>No analysis tools were enabled for this submission.</Text>
                        </Box>
                      )}
                    </VStack>
                  </TabPanel>

                  {/* Output Tab */}
                  <TabPanel p={4} bg={bgColor}>
                    <StdoutDisplay response={submissionData?.response || null} />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button 
              colorScheme="green" 
              onClick={handleRestore}
              isLoading={isRestoring}
              loadingText="Restoring..."
            >
              <Icon as={RepeatIcon} mr={2} />
              Restore Code & Results
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default function SubmissionHistory({ problemId, onRestoreSubmission }: SubmissionHistoryProps) {
  const textColor = useColorModeValue('gray.800', 'gray.200')
  const { getAccessToken } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  
  const [selectedSubmission, setSelectedSubmission] = useState<EnhancedPublicSubmissionData | null>(null)
  const [selectedSubmissionName, setSelectedSubmissionName] = useState<string>('')
  const [editingName, setEditingName] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const [showAll, setShowAll] = useState(false)
  
  const { isOpen: isViewModalOpen, onOpen: onViewModalOpen, onClose: onViewModalClose } = useDisclosure()
  const [isViewingLoading, setIsViewingLoading] = useState(false)

  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const bgColor = useColorModeValue('gray.50', 'gray.700')
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400')
  // Reserved for future theme refinements of badges
  // const chipBg = useColorModeValue('gray.100', 'gray.600')
  // const inputChipBg = useColorModeValue('blue.50', 'blue.900')
  // const targetChipBg = useColorModeValue('purple.50', 'purple.900')

  // Fetch submission history
  const { data: submissionHistory, isLoading, error, refetch } = useQuery(
    ['submissionHistory', problemId],
    async () => {
      await getAccessToken()
      const response = await submissionHistoryService.getSubmissionHistory(problemId)
      return response
    },
    {
      enabled: !!problemId,
      retry: 2,
    }
  )

  // Mutation for updating submission name
  const updateNameMutation = useMutation(
    async ({ submissionId, name }: { submissionId: string; name: string }) => {
      await getAccessToken()
      await submissionHistoryService.updateSubmissionName(submissionId, name)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['submissionHistory', problemId])
        toast({
          title: 'Success',
          description: 'Submission name updated',
          status: 'success',
          duration: 3000,
        })
        setEditingName(null)
      },
      onError: () => {
        toast({
          title: 'Error',
          description: 'Failed to update submission name',
          status: 'error',
          duration: 3000,
        })
      }
    }
  )

  // Mutation for bookmarking submissions
  const bookmarkMutation = useMutation(
    async ({ submissionId, action }: { submissionId: string; action: 'add' | 'remove' }) => {
      await getAccessToken()
      await submissionHistoryService.bookmarkSubmission(submissionId, action)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['submissionHistory', problemId])
        toast({
          title: 'Success',
          description: 'Bookmark updated',
          status: 'success',
          duration: 2000,
        })
      },
      onError: () => {
        toast({
          title: 'Error',
          description: 'Failed to update bookmark',
          status: 'error',
          duration: 3000,
        })
      }
    }
  )

  const handleViewSubmission = async (submissionId: string) => {
    try {
      // Open modal immediately with loading state
      setIsViewingLoading(true)
      onViewModalOpen()

      await getAccessToken()
      const submissionData = await submissionHistoryService.getSubmissionData(submissionId)
      // Normalize response inputs to strip .json for consistent matching
      if (submissionData && submissionData.response) {
        submissionData.response = normalizeCombinedResponseInputs(submissionData.response)
      }

      // Find the original submission from the list to get the correct name
      const originalSubmission = submissions.find(s => s.submission_id === submissionId)
      const submissionName = originalSubmission ? getDisplayName(originalSubmission) : 'Unknown Submission'

      setSelectedSubmission(submissionData)
      setSelectedSubmissionName(submissionName)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load submission data',
        status: 'error',
        duration: 3000,
      })
    } finally {
      setIsViewingLoading(false)
    }
  }

  const handleRestoreSubmission = (sourceCode: string, response: any) => {
    if (onRestoreSubmission) {
      onRestoreSubmission(sourceCode, response)
      toast({
        title: 'Success',
        description: 'Code and results restored to editor',
        status: 'success',
        duration: 3000,
      })
    }
  }

  const handleDirectRestore = async (submissionId: string) => {
    try {
      await getAccessToken()
      const submissionData = await submissionHistoryService.getSubmissionData(submissionId)
      const normalized = submissionData && submissionData.response
        ? normalizeCombinedResponseInputs(submissionData.response)
        : submissionData.response
      handleRestoreSubmission(submissionData.source_code, normalized)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to restore submission',
        status: 'error',
        duration: 3000,
      })
    }
  }

  const handleEditName = (submission: SubmissionList['submission_list'][0]) => {
    setEditingName(submission.submission_id)
    setNewName(getSubmissionName(submission))
  }

  const handleSaveName = (submissionId: string) => {
    if (newName.trim()) {
      updateNameMutation.mutate({ submissionId, name: newName.trim() })
    }
  }

  const handleBookmark = (submission: SubmissionList['submission_list'][0]) => {
    const isBookmarked = isSubmissionBookmarked(submission)
    const action = isBookmarked ? 'remove' : 'add'
    bookmarkMutation.mutate({ submissionId: submission.submission_id, action })
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'success':
      case 'completed':
        return 'green'
      case 'running':
      case 'processing':
        return 'blue'
      case 'failed':
      case 'error':
        return 'red'
      default:
        return 'gray'
    }
  }

  const extractInputs = (submission: SubmissionList['submission_list'][0]): string[] => {
    const inputs: string[] = []
    if (submission.tags) {
      submission.tags.forEach(t => {
        if (t.tag_type === 'input') inputs.push(t.key)
      })
    }
    // Fallback: infer from results meta num_inputs or benchmark custom_score_name keys
    if (inputs.length === 0 && submission.submission_results) {
      const inputKeys = new Set<string>()
      submission.submission_results.forEach(r => {
        if (r.result_type?.startsWith('benchmark_') && r.key) inputKeys.add(r.key)
      })
      inputs.push(...Array.from(inputKeys))
    }
    return inputs
  }

  const extractTargets = (submission: SubmissionList['submission_list'][0]): string[] => {
    const targets: string[] = []
    if (submission.tags) {
      submission.tags.forEach(t => {
        if (t.tag_type === 'target') targets.push(t.key)
      })
    }
    // Fallback: infer from results
    const has = (type: string) => submission.submission_results?.some(r => r.result_type === type)
    if (has('cilksan') && !targets.includes('cilksan')) targets.push('cilksan')
    if ((has('cilkscale_benchmark') || submission.submission_results?.some(r => r.result_type === 'benchmark' && r.key === 'cilkscale')) && !targets.includes('cilkscale')) targets.push('cilkscale')
    if (has('profiler') && !targets.includes('perf-profiler')) targets.push('perf-profiler')
    if (submission.submission_results?.some(r => r.result_type === 'correctness') && !targets.includes('correctness')) targets.push('correctness')
    if (submission.submission_results?.some(r => r.result_type === 'benchmark') && !targets.includes('benchmark')) targets.push('benchmark')
    return targets
  }

  const countInputs = (submission: SubmissionList['submission_list'][0]): number => {
    return extractInputs(submission).length
  }

  const countTargets = (submission: SubmissionList['submission_list'][0]): number => {
    return extractTargets(submission).length
  }

  const getAnalysisTargets = (submission: SubmissionList['submission_list'][0]): string[] => {
    return extractTargets(submission).filter(t => t !== 'correctness' && t !== 'benchmark')
  }

  const countAnalysisTools = (submission: SubmissionList['submission_list'][0]): number => {
    // Defined as N-2 where N is number of targets (correctness and benchmark always done)
    const n = countTargets(submission)
    return Math.max(0, n - 2)
  }

  if (isLoading) {
    return (
      <VStack align="stretch" spacing={3}>
        <HStack>
          <Icon as={ChatIcon} color="purple.400" />
          <Heading size="sm" color={textColor}>Submission History</Heading>
        </HStack>
        <Box display="flex" justifyContent="center" py={8}>
          <Spinner size="md" />
        </Box>
      </VStack>
    )
  }

  if (error) {
    return (
      <VStack align="stretch" spacing={3}>
        <HStack>
          <Icon as={ChatIcon} color="purple.400" />
          <Heading size="sm" color={textColor}>Submission History</Heading>
        </HStack>
        <Alert status="error">
          <AlertIcon />
          Failed to load submission history. Please try again.
        </Alert>
        <Button size="sm" onClick={() => refetch()}>
          Retry
        </Button>
      </VStack>
    )
  }

  const submissions = submissionHistory?.submission_list || []
  const MAX_VISIBLE = 20
  // Split bookmarked and regular lists
  const bookmarkedSubmissions = submissions.filter(isSubmissionBookmarked)
  const displayedSubmissions = showAll ? submissions : submissions.slice(0, MAX_VISIBLE)

  return (
    <VStack align="stretch" spacing={3}>
      <HStack justify="space-between">
        <HStack>
          <Icon as={ChatIcon} color="purple.400" />
          <Heading size="sm" color={textColor}>Submission History</Heading>
        </HStack>
        <Button size="xs" variant="outline" onClick={() => refetch()}>
          Refresh
        </Button>
      </HStack>
      
      <VStack spacing={2} align="stretch">
        {submissions.length === 0 ? (
          <Text fontSize="sm" color={textColor}>
            No submissions yet
          </Text>
        ) : (
          <>
          {/* Bookmarked Section */}
          {bookmarkedSubmissions.length > 0 && (
            <VStack align="stretch" spacing={1}>
              <HStack pt={1} pb={1}>
                <Icon as={AiFillStar} color="yellow.400" />
                <Text fontSize="sm" fontWeight="semibold" color={textColor}>Bookmarked</Text>
              </HStack>
              {bookmarkedSubmissions.map((submission, index) => (
                <Box
                  key={`bm-${submission.submission_id}-${index}`}
                  p={3}
                  border="1px"
                  borderColor={borderColor}
                  borderRadius="md"
                  bg={bgColor}
                >
                  <VStack align="stretch" spacing={2}>
                    {/* Header with name and controls */}
                    <HStack justify="space-between">
                      <VStack align="start" spacing={1} flex={1}>
                        {editingName === submission.submission_id ? (
                          <HStack spacing={2}>
                            <Input
                              size="xs"
                              value={newName}
                              onChange={(e) => setNewName(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleSaveName(submission.submission_id)
                                }
                              }}
                              placeholder="Enter submission name..."
                            />
                            <Button size="xs" colorScheme="green" onClick={() => handleSaveName(submission.submission_id)}>
                              Save
                            </Button>
                            <Button size="xs" variant="outline" onClick={() => setEditingName(null)}>
                              Cancel
                            </Button>
                          </HStack>
                        ) : (
                          <HStack>
                            {hasCustomName(submission) ? (
                              <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                                {getSubmissionName(submission)}
                              </Text>
                            ) : (
                              <Text fontSize="sm" fontWeight="medium" color={mutedTextColor}>
                                {getDisplayName(submission)}
                              </Text>
                            )}
                            <Button size="xs" variant="ghost" onClick={() => handleEditName(submission)}>
                              <Icon as={EditIcon} boxSize={3} />
                            </Button>
                          </HStack>
                        )}
                        <HStack spacing={2}>
                          <Text fontSize="xs" color={mutedTextColor}>
                            {formatDate(submission.time_created)}
                          </Text>
                          <HStack spacing={2}>
                            <Tooltip label={getAnalysisTargets(submission).join(', ') || 'No analysis tools'}>
                              <Tag size="sm" colorScheme="purple" variant="subtle">
                                <TagLeftIcon as={SettingsIcon} />
                                <TagLabel>{countAnalysisTools(submission)} tools</TagLabel>
                              </Tag>
                            </Tooltip>
                            <Tooltip label={extractInputs(submission).join(', ') || 'No inputs'}>
                              <Tag size="sm" colorScheme="blue" variant="outline">
                                <TagLeftIcon as={AttachmentIcon} />
                                <TagLabel>{countInputs(submission)} inputs</TagLabel>
                              </Tag>
                            </Tooltip>
                          </HStack>
                        </HStack>
                      </VStack>
                      <VStack align="end" spacing={1}>
                        <Badge colorScheme={getStatusColor(submission.status)} size="sm">
                          {submission.status}
                        </Badge>
                        <Button
                          size="xs"
                          variant="ghost"
                          colorScheme={isSubmissionBookmarked(submission) ? "yellow" : "gray"}
                          onClick={() => handleBookmark(submission)}
                        >
                          <Icon as={AiFillStar} boxSize={3} />
                        </Button>
                      </VStack>
                    </HStack>

                    {/* Spark Chart for performance summary */}
                    <Box>
                      <SubmissionSparkChart submission={submission} isCompact={true} />
                    </Box>
                  </VStack>
                  
                  <HStack spacing={2}>
                    <Button 
                      size="xs" 
                      colorScheme="blue" 
                      variant="outline"
                      leftIcon={<ViewIcon />}
                      onClick={() => handleViewSubmission(submission.submission_id)}
                    >
                      View
                    </Button>
                    {onRestoreSubmission && (
                      <Button 
                        size="xs" 
                        colorScheme="green" 
                        variant="outline"
                        leftIcon={<RepeatIcon />}
                        onClick={() => handleDirectRestore(submission.submission_id)}
                      >
                        Restore
                      </Button>
                    )}
                  </HStack>
                </Box>
              ))}
              {/* Divider between sections */}
              <HStack pt={1} pb={1}>
                <Divider />
              </HStack>
            </VStack>
          )}

          {/* All Submissions Section (including bookmarked) */}
          <HStack pt={1} pb={1}>
            <Icon as={ChatIcon} color="purple.400" />
            <Text fontSize="sm" fontWeight="semibold" color={textColor}>All submissions</Text>
          </HStack>
          {displayedSubmissions.map((submission, index) => (
            <Box
              key={`${submission.submission_id}-${index}`}
              p={2}
              border="1px"
              borderColor={borderColor}
              borderRadius="md"
              bg={bgColor}
            >
              <VStack align="stretch" spacing={2}>
                {/* Header with name and controls */}
                <HStack justify="space-between">
                  <VStack align="start" spacing={1} flex={1}>
                    {editingName === submission.submission_id ? (
                      <HStack spacing={2}>
                        <Input
                          size="xs"
                          value={newName}
                          onChange={(e) => setNewName(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveName(submission.submission_id)
                            }
                          }}
                          placeholder="Enter submission name..."
                        />
                        <Button size="xs" colorScheme="green" onClick={() => handleSaveName(submission.submission_id)}>
                          Save
                        </Button>
                        <Button size="xs" variant="outline" onClick={() => setEditingName(null)}>
                          Cancel
                        </Button>
                      </HStack>
                    ) : (
                      <HStack>
                        <Text fontSize="sm" fontWeight={hasCustomName(submission) ? 'semibold' : 'medium'} color={hasCustomName(submission) ? textColor : mutedTextColor}>
                          {hasCustomName(submission) ? getSubmissionName(submission) : getDisplayName(submission)}
                        </Text>
                        <Button size="xs" variant="ghost" onClick={() => handleEditName(submission)}>
                          <Icon as={EditIcon} boxSize={3} />
                        </Button>
                      </HStack>
                    )}
                    <HStack spacing={2}>
                      <Text fontSize="xs" color={mutedTextColor}>
                        {formatDate(submission.time_created)}
                      </Text>
                      <HStack spacing={2}>
                        <Tooltip label={getAnalysisTargets(submission).join(', ') || 'No analysis tools'}>
                          <Tag size="sm" colorScheme="purple" variant="subtle">
                            <TagLeftIcon as={SettingsIcon} />
                            <TagLabel>{countAnalysisTools(submission)} tools</TagLabel>
                          </Tag>
                        </Tooltip>
                        <Tooltip label={extractInputs(submission).join(', ') || 'No inputs'}>
                          <Tag size="sm" colorScheme="blue" variant="outline">
                            <TagLeftIcon as={AttachmentIcon} />
                            <TagLabel>{countInputs(submission)} inputs</TagLabel>
                          </Tag>
                        </Tooltip>
                      </HStack>
                    </HStack>
                  </VStack>
                  <VStack align="end" spacing={1}>
                    <Badge colorScheme={getStatusColor(submission.status)} size="sm">
                      {submission.status}
                    </Badge>
                    <IconButton
                      aria-label={isSubmissionBookmarked(submission) ? 'Remove bookmark' : 'Bookmark'}
                      size="xs"
                      variant="ghost"
                      colorScheme={isSubmissionBookmarked(submission) ? "yellow" : "gray"}
                      onClick={() => handleBookmark(submission)}
                      icon={<Icon as={isSubmissionBookmarked(submission) ? AiFillStar : AiOutlineStar} boxSize={3} />}
                    />
                  </VStack>
                </HStack>

                {/* Spark Chart for performance summary */}
                <Box>
                  <SubmissionSparkChart submission={submission} isCompact={true} />
                </Box>
              </VStack>
              
              <HStack spacing={2}>
                <Button 
                  size="xs" 
                  colorScheme="blue" 
                  variant="outline"
                  leftIcon={<ViewIcon />}
                  onClick={() => handleViewSubmission(submission.submission_id)}
                >
                  View
                </Button>
                {onRestoreSubmission && (
                  <Button 
                    size="xs" 
                    colorScheme="green" 
                    variant="outline"
                    leftIcon={<RepeatIcon />}
                    onClick={() => handleDirectRestore(submission.submission_id)}
                  >
                    Restore
                  </Button>
                )}
              </HStack>
            </Box>
          ))}
          {submissions.length > MAX_VISIBLE && (
            <HStack justify="center" pt={2}>
              <Button size="xs" onClick={() => setShowAll(s => !s)}>
                {showAll ? 'Show less' : `Show more (${submissions.length - MAX_VISIBLE})`}
              </Button>
            </HStack>
          )}
          </>
        )}
      </VStack>

      {/* View Submission Modal */}
      <ViewSubmissionModal
        isOpen={isViewModalOpen}
        onClose={onViewModalClose}
        submissionData={selectedSubmission}
        submissionName={selectedSubmissionName}
        onRestore={handleRestoreSubmission}
        isLoading={isViewingLoading}
      />
    </VStack>
  )
} 